import { useEffect } from 'react';
import { haptic, setVerticalSwipes, tg } from './telegram';
import { askBeforeClosing, backAction, nextUnsaved, showBack, type LayerInfo, type Unsaved } from './nav-back';

/**
 * Back, the one place for the whole app (App.tsx runs it once).
 *
 * Coaches kept leaving the app by accident: the «‹ Клиенты» links were
 * small, Telegram's top-left said «Close», and a swipe down closed it.
 * Now, in Telegram:
 *
 *   - its own back arrow shows whenever something is over the main tabs —
 *     a sheet, the client card, a Settings page — and goes back one level;
 *     on Android the hardware back key does the same while it shows;
 *   - swipe-down-to-close is off (7.7+);
 *   - it asks before closing only while something typed is unsaved.
 *
 * The layers are read from the page, not registered screen by screen:
 * every sheet is a `.sheet`, a pushed screen is `[data-layer]`, and a
 * layer's own way back («Закрыть», «‹ Клиенты») is `[data-back]`. A
 * sheet without one closes on a tap on its backdrop, which is what back
 * presses. The decisions are in nav-back.ts.
 */

const LAYER = '.sheet, [data-layer]';

/** The open layers, bottom to top, each with the element back would press. */
function readLayers() {
  const els = [...document.querySelectorAll<HTMLElement>(LAYER)]
    // A layer inside a sheet's panel is part of that sheet.
    .filter((el) => el.classList.contains('sheet') || !el.parentElement?.closest('.sheet'))
    // A screen kept underneath the one on top (CoachScreen's history) is hidden: not a layer.
    .filter((el) => !el.closest('[hidden]'));
  return els.map((el) => {
    const own = [...el.querySelectorAll<HTMLElement>('[data-back]')].find((b) => b.closest(LAYER) === el) ?? null;
    const info: LayerInfo = { kind: el.classList.contains('sheet') ? 'sheet' : 'screen', hasBack: own !== null };
    return { el, own, info };
  });
}

let ids = new WeakMap<Element, string>();
let next = 0;
const idOf = (el: Element) => {
  if (!ids.has(el)) ids.set(el, `layer${next++}`);
  return ids.get(el)!;
};
const layerOf = (target: EventTarget | null) => {
  const el = target instanceof Element ? target.closest(LAYER) : null;
  return el ? idOf(el) : null;
};

/** Telegram's closing question (6.2+); older clients have none. */
function setClosingConfirmation(on: boolean) {
  const w = tg as any;
  if (!w?.isVersionAtLeast?.('6.2')) return;
  try { on ? w.enableClosingConfirmation() : w.disableClosingConfirmation(); } catch { /* unsupported */ }
}

export function useNavBack() {
  useEffect(() => {
    const button = (tg as any)?.isVersionAtLeast?.('6.1') ? tg!.BackButton : null;
    let unsaved: Unsaved = new Set();
    let shown = false, asking = false, queued = 0;

    const sync = () => {
      queued = 0;
      const layers = readLayers();
      unsaved = nextUnsaved(unsaved, { type: 'open', layers: layers.map((l) => idOf(l.el)) });
      const show = showBack(layers.map((l) => l.info));
      if (button && show !== shown) { show ? button.show() : button.hide(); shown = show; }
      const ask = askBeforeClosing(unsaved);
      if (ask !== asking) { setClosingConfirmation(ask); asking = ask; }
    };
    const later = () => { if (!queued) queued = requestAnimationFrame(sync); };

    const onBack = () => {
      const layers = readLayers();
      const act = backAction(layers.map((l) => l.info));
      if (!act) return;
      haptic.tap();
      const { el, own } = layers[act.layer];
      (act.via === 'back' ? own : el)?.click();
    };
    const onInput = (e: Event) => {
      const t = e.target as HTMLInputElement | HTMLTextAreaElement | null;
      if (!t || !('value' in t)) return;
      const field = t.tagName === 'TEXTAREA' ? 'text' : t.type === 'search' ? 'search' : t.type === 'text' ? 'text' : 'other';
      unsaved = nextUnsaved(unsaved, { type: 'input', layer: layerOf(t), field, skip: !!t.closest('[data-nodirty]') });
      later();
    };
    const onClick = (e: Event) => {
      const b = e.target instanceof Element ? e.target.closest('.savebtn, [data-save]') : null;
      if (!b) return;
      unsaved = nextUnsaved(unsaved, { type: 'save', layer: layerOf(b) });
      later();
    };

    // Swipe-down-to-close off for good (7.7+): a coach scrolling a long
    // list up from the top kept closing the app.
    setVerticalSwipes(false);
    button?.onClick(onBack);
    const watch = new MutationObserver(later);
    watch.observe(document.body, { childList: true, subtree: true });
    document.addEventListener('input', onInput, true);
    document.addEventListener('click', onClick, true);
    sync();
    return () => {
      button?.offClick(onBack);
      button?.hide();
      watch.disconnect();
      document.removeEventListener('input', onInput, true);
      document.removeEventListener('click', onClick, true);
      if (queued) cancelAnimationFrame(queued);
      setClosingConfirmation(false);
      ids = new WeakMap();
    };
  }, []);
}
